import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { getQueryString } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";

const PAGE_SIZE = 20;

export interface InfiniteOption {
  value: string | number;
  label: string;
}

interface OptionsPage {
  items: any[];
  hasMore: boolean;
}

interface OptionsState<T> {
  sourcesKey: string;
  urlIndex: number;
  pageIndex: number;
  mergedPage: string;
  options: T[];
}

const createOptionsState = <T>(sourcesKey: string): OptionsState<T> => ({
  sourcesKey,
  urlIndex: 0,
  pageIndex: 0,
  mergedPage: "",
  options: [],
});

const SWR_KEY_PREFIX = "infinite-options";

const fetchOptionsPage = async ([, url, pageIndex]: [
  string,
  string,
  number,
]): Promise<OptionsPage> => {
  const response = await fetcher<any>(
    getQueryString(url, {
      pageIndex: String(pageIndex),
      pageSize: String(PAGE_SIZE),
    }),
    MethodType.GET,
  );
  if (Array.isArray(response)) return { items: response, hasMore: false };
  const items: any[] = response?.output || [];
  const total = Number(
    response?.metadata?.total_elements ?? response?.metadata?.totalElements,
  );
  return {
    items,
    hasMore: Number.isFinite(total)
      ? (pageIndex + 1) * PAGE_SIZE < total
      : items.length === PAGE_SIZE,
  };
};

export const useInfiniteOptions = <T extends InfiniteOption>(
  urls: string[],
  toOption: (item: any, urlIndex: number) => T,
) => {
  const sourcesKey = urls.join("|");
  const toOptionRef = useRef(toOption);
  toOptionRef.current = toOption;

  const [stored, setStored] = useState<OptionsState<T>>(() =>
    createOptionsState<T>(sourcesKey),
  );
  const state =
    stored.sourcesKey === sourcesKey
      ? stored
      : createOptionsState<T>(sourcesKey);

  const url = urls[state.urlIndex];
  const pageKey = `${sourcesKey}#${state.urlIndex}#${state.pageIndex}`;
  const swrKey: [string, string, number] | null = url
    ? [SWR_KEY_PREFIX, url, state.pageIndex]
    : null;
  const { data: page, isLoading } = useSWR(swrKey, fetchOptionsPage);

  useEffect(() => {
    if (!page) return;
    setStored((prev) => {
      const base =
        prev.sourcesKey === sourcesKey
          ? prev
          : createOptionsState<T>(sourcesKey);
      if (base.mergedPage === pageKey) return prev;
      const known = new Set(base.options.map((option) => option.value));
      const added = page.items
        .map((item) => toOptionRef.current(item, base.urlIndex))
        .filter((option) => !known.has(option.value));
      return {
        ...base,
        mergedPage: pageKey,
        options: [...base.options, ...added],
      };
    });
  }, [page, pageKey, sourcesKey]);

  const merged = state.mergedPage === pageKey;
  const hasNextSource = state.urlIndex < urls.length - 1;

  useEffect(() => {
    if (!merged || !page || page.hasMore || !hasNextSource) return;
    setStored({ ...state, urlIndex: state.urlIndex + 1, pageIndex: 0 });
  }, [merged, page, hasNextSource, state]);

  const loadMore = () => {
    if (!merged || !page?.hasMore) return;
    setStored({ ...state, pageIndex: state.pageIndex + 1 });
  };

  return { options: state.options, loading: isLoading, loadMore };
};
